import React, { useContext, useMemo, useState } from 'react';
import { useProducts } from '../ProductContext';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { usePrinting } from '../PrintingContext';
import { useCash } from '../CashContext';
import { useOtherServiceSales } from '../OtherServiceSalesContext';
import { wakalaCashbookRows } from '../wakalaConstants';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';

const BusinessReportPage = () => {
  const { products, inventoryLoading } = useProducts();
  const { salesHistory, salesLoading } = useContext(SalesHistoryContext);
  const { printJobs } = usePrinting();
  const { floatBalance, transactions } = useCash();
  const { sales: otherServiceSales } = useOtherServiceSales();
  const [filter, setFilter] = useState('');

  const summary = useMemo(() => {
    const sales = Array.isArray(salesHistory) ? salesHistory : [];
    const posRevenue = sales.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    const posProfit = sales.reduce((sum, s) => {
      const p = parseFloat(s.profit);
      if (!Number.isNaN(p)) return sum + p;
      const a = parseFloat(s.amount);
      const c = parseFloat(s.cost);
      if (!Number.isNaN(a) && !Number.isNaN(c)) return sum + (a - c);
      return sum;
    }, 0);
    const jobs = Array.isArray(printJobs) ? printJobs : [];
    const printBilled = jobs.reduce((sum, j) => sum + (Number(j.totalAmount) || 0), 0);
    const printCollected = jobs.reduce((sum, j) => sum + (Number(j.paidAmount) || 0), 0);
    const txs = Array.isArray(transactions) ? transactions : [];
    const txsWakala = wakalaCashbookRows(txs);
    const cashIn = txsWakala.filter((t) => t.direction === 'in').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const cashOut = txsWakala.filter((t) => t.direction === 'out').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const otherRev = (Array.isArray(otherServiceSales) ? otherServiceSales : []).reduce(
      (sum, o) => sum + (Number(o.amountTzs) || 0),
      0
    );
    return [
      { id: 1, name: 'POS revenue (recorded sales)', value: posRevenue },
      { id: 10, name: 'POS profit (recorded sales)', value: posProfit },
      { id: 2, name: 'POS transactions', value: sales.length },
      { id: 3, name: 'Products in stock list', value: Array.isArray(products) ? products.length : 0 },
      { id: 4, name: 'Printing total billed (TZS)', value: printBilled },
      { id: 5, name: 'Printing total collected (TZS)', value: printCollected },
      { id: 11, name: 'Other services revenue (TZS)', value: otherRev },
      { id: 6, name: 'Wakala cash-in volume (TZS)', value: cashIn },
      { id: 7, name: 'Wakala cash-out volume (TZS)', value: cashOut },
      { id: 8, name: 'Current float balance (TZS)', value: Number(floatBalance) || 0 },
      { id: 9, name: 'Cashbook rows', value: txs.length },
    ];
  }, [salesHistory, products, printJobs, transactions, floatBalance, otherServiceSales]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((row) => String(row.name).toLowerCase().includes(q));
  }, [summary, filter]);

  const exportHead = ['Metric', 'Value'];
  const exportRows = useMemo(() => filtered.map((r) => [r.name, r.value]), [filtered]);

  const loading = inventoryLoading || salesLoading;

  if (loading) {
    return (
      <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
        <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Business Summary Report</h1>
        <p style={{ color: '#64748b' }}>Loading summary data…</p>
      </div>
    );
  }

  return (
    <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
      <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Business Summary Report</h1>
      <p style={{ color: '#64748b', marginBottom: 16 }}>
        Aggregates from live POS sales, inventory, printing jobs, and wakala cashbook (same data as the main app).
      </p>
      <div style={{ marginBottom: 20 }}>
        <label htmlFor="biz-filter">Filter metrics: </label>
        <input
          id="biz-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Type to filter…"
          style={{ marginLeft: 8, padding: '8px 12px', minWidth: 240 }}
        />
      </div>
      <table style={{ background: '#fff', borderRadius: 8, width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 12 }}>Metric</th>
            <th style={{ textAlign: 'right', padding: 12 }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => (
            <tr key={row.id}>
              <td style={{ padding: 12 }}>{row.name}</td>
              <td style={{ padding: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {typeof row.value === 'number' && row.name.includes('transactions')
                  ? row.value.toLocaleString()
                  : typeof row.value === 'number' &&
                      (row.name.includes('revenue') ||
                        row.name.includes('profit') ||
                        row.name.includes('TZS') ||
                        row.name.includes('float') ||
                        row.name.includes('volume') ||
                        row.name.includes('billed') ||
                        row.name.includes('collected'))
                    ? `TZS ${Number(row.value).toLocaleString()}`
                    : typeof row.value === 'number'
                      ? row.value.toLocaleString()
                      : row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 20 }}>
        <button type="button" onClick={() => downloadCsv('business-summary.csv', [exportHead, ...exportRows])} style={{ marginRight: 10 }}>
          Export to Excel
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              await exportTableToPdf('Business Summary', exportHead, exportRows);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default BusinessReportPage;
